/* eslint-disable no-restricted-syntax */
import React, { Component, createContext } from 'react';
import { identity, find, isPlainObject } from 'lodash/fp';
import invariant from 'utils/invariant';

export const FilterContext = createContext();

export const FilterConsumer = FilterContext.Consumer;

export class FilterProvider extends Component {
  static defaultProps = {
    toParams: () => ({}),
  };

  constructor(props) {
    super(props);

    invariant(
      isPlainObject(props.initialState),
      'FilterProvider requires an initialState prop that is a plain object.',
    );

    // Rule is disabled because we pass the entire state object to the context
    // as its value
    /* eslint-disable react/no-unused-state */
    this.state = {
      filterRegistry: this.filterRegistry,
      filters: props.initialState,
      getSelectedFilters: this.getSelectedFilters,
      registerFilter: this.registerFilter,
      setFilterValue: this.setFilterValue,
      removeSelected: this.removeSelected,
      resetAllFilters: this.resetAllFilters,
      subscribeToRegistry: this.addRegistrySubscription,
      params: props.toParams(props.initialState),
    };
  }

  /**
   * A registry to store all filter implementations under this context provider.
   */
  filterRegistry = new Map();

  /**
   * Registers a filter under this context provider.
   */
  registerFilter = filter => {
    if (process.env.NODE_ENV === 'development') {
      invariant(
        filter.name in this.state.filters,
        `You attempted to register a filter of name "${filter.name}" without specifying an initial value in the \`initialState\` prop ` +
          'of FilterProvider. Have you misspelled the name?',
      );
    }

    // Setting defaults
    filter.mapValueToSelected = filter.mapValueToSelected || identity;

    this.filterRegistry.set(filter.name, filter);
    this.flushRegistrySubscriptions();
  };

  /**
   * An array to store all subscriptions that will be notified if a filter is
   * added or changed. Since filter implementation will not be available at
   * first render, this is useful in case other parts of the app are dependant
   * on results coming from the filter implementation itself.
   *
   * @see FilterProvider#registerFilter
   * @see FilterProvider#addRegistrySubscription
   * @see FilterProvider#flushRegistrySubscriptions
   */
  registrySubscriptions = [];

  /**
   * Adds a new registry subscription
   */
  addRegistrySubscription = subscription => {
    this.registrySubscriptions.push(subscription);
  };

  /**
   * Notifies all registry subscriptions of a filter addition or a change.
   */
  flushRegistrySubscriptions() {
    if (this.registrySubscriptions.length) {
      this.registrySubscriptions.forEach(subscription => subscription());
      this.registrySubscriptions = [];
    }
  }

  /**
   * Adds or updates a filter value with the specified name
   * and value in the provider values state.
   */
  setFilterValue = (name, value) => {
    const { toParams } = this.props;
    this.setState(state => {
      const filters = { ...state.filters, [name]: value };
      return { filters, params: toParams(filters) };
    });
  };

  /**
   * Remove all filter values in the provider state by setting them to the
   * corresponding defaultState
   */
  resetAllFilters = () => {
    for (const [name, filter] of this.filterRegistry.entries()) {
      this.setFilterValue(name, filter.defaultState);
    }
  };

  /**
   * An array that holds reference to the last computed result of
   * `getSelectedFilters`. Necessary for the purpose diffing to see what values
   * were added so they get pushed to the end of the next call result.
   *
   * @see FilterProvider#getSelectedFilters
   */
  lastSelected = [];

  /**
   * Returns an array of selected filters objects with the return value of the
   * corresponding filter's mapValueToSelected. If mapValueToSelected is
   * not specified, the filter value will be combined with the array as is. If
   * mapValueToSelected returns a falsy value, the selected filter will not be
   * included in the selected filters array.
   *
   * Each selected filter value will be an object with a type property set to
   * the filter name, and a data property set the filter value.
   */
  getSelectedFilters = () => {
    const { filters } = this.state;
    const selectedFilters = [];
    for (const [name, filter] of this.filterRegistry.entries()) {
      const value = filter.mapValueToSelected(filters[name]);
      if (value) {
        const items = Array.isArray(value) ? value : [value];
        const selectedItems = items.map(data => ({ type: name, data }));
        selectedFilters.push(...selectedItems);
      }
    }
    const nextSelected = [
      ...this.lastSelected.filter(selected => find(selected, selectedFilters)),
      ...selectedFilters.filter(selected => !find(selected, this.lastSelected)),
    ];
    this.lastSelected = nextSelected;
    return nextSelected;
  };

  /**
   * Removes a selected filter object from the filter state. The new filter
   * state is determined by the filter itself using the return value of
   * filter.removeSelected.
   */
  removeSelected = selected => {
    const filter = this.filterRegistry.get(selected.type);
    this.setFilterValue(
      filter.name,
      filter.removeSelected(selected.data, this.state.filters[filter.name]),
    );
  };

  render() {
    return (
      <FilterContext.Provider value={this.state}>
        {this.props.children}
      </FilterContext.Provider>
    );
  }
}
